/* Preliminary construction cost — the conversation every project starts with:
   "what can I build for this?"

   The studio owns the rates (cost per unit area, by building type and level of
   finish). The client owns the budget. Between them, area falls out — and from
   the area, an honest answer about what actually fits. Nothing here is a
   quotation; it's the sizing conversation, done with real numbers.

   As projects finish with a costed plan, `learnedRates()` reads what things
   really cost and offers that back — so the rate card gets truer over time
   instead of drifting from reality. */

import type { Project, SpaceReq } from "./types";

export type AreaUnit = "sqft" | "sqm";
export type Quality = "standard" | "premium" | "bespoke";

export interface BuildRate {
  type: string;                 // building type this applies to
  unit: AreaUnit;
  standard: number;             // cost per unit area
  premium: number;
  bespoke: number;
}

export const QUALITY_LABEL: Record<Quality, string> = {
  standard: "Standard", premium: "Premium", bespoke: "Bespoke",
};
export const QUALITY_BLURB: Record<Quality, string> = {
  standard: "Sound, well-built, conventional materials and systems.",
  premium: "Better finishes, more joinery, more specification control.",
  bespoke: "Made-to-measure throughout — bespoke joinery, imported finishes, high servicing.",
};

/* Starting rates, per ft². Deliberately conservative and clearly the studio's
   own assumption — they are meant to be edited on the Rates page, and to be
   corrected over time by what the studio's own finished projects actually cost. */
export const DEFAULT_BUILD_RATES: BuildRate[] = [
  { type: "Private Residence", unit: "sqft", standard: 165, premium: 240, bespoke: 360 },
  { type: "Apartment Building", unit: "sqft", standard: 145, premium: 200, bespoke: 290 },
  { type: "Mixed-Use Development", unit: "sqft", standard: 150, premium: 210, bespoke: 300 },
  { type: "Workplace / Office", unit: "sqft", standard: 130, premium: 185, bespoke: 265 },
  { type: "Commercial / Retail", unit: "sqft", standard: 120, premium: 170, bespoke: 245 },
  { type: "Restaurant / F&B", unit: "sqft", standard: 175, premium: 250, bespoke: 340 },
  { type: "Shopping Centre / Mall", unit: "sqft", standard: 135, premium: 185, bespoke: 250 },
  { type: "Hospitality / Hotel", unit: "sqft", standard: 190, premium: 270, bespoke: 380 },
  { type: "Church / Place of Worship", unit: "sqft", standard: 140, premium: 195, bespoke: 275 },
  { type: "Healthcare", unit: "sqft", standard: 220, premium: 300, bespoke: 400 },
  { type: "Education", unit: "sqft", standard: 155, premium: 215, bespoke: 290 },
  { type: "Civic / Institutional", unit: "sqft", standard: 175, premium: 240, bespoke: 330 },
  { type: "Industrial / Warehouse", unit: "sqft", standard: 75, premium: 105, bespoke: 145 },
  { type: "Logistics & Distribution", unit: "sqft", standard: 70, premium: 95, bespoke: 130 },
  { type: "Interior Fit-out", unit: "sqft", standard: 95, premium: 155, bespoke: 240 },
];

const SQFT_PER_SQM = 10.7639;
export const unitLabel = (u: AreaUnit) => (u === "sqm" ? "m²" : "ft²");

/* ---- money: the rate card is written in ONE currency ----------------------
   A rate of 120 is 120 of something. The card says which (USD unless the
   studio changes it), and a project priced in another currency has the rate
   converted before anything is divided — otherwise a GYD budget gets divided
   by a USD rate and the area comes out two hundred times too big. */
export const DEFAULT_RATE_CURRENCY = "USD";
/** Units of each currency per 1 USD. Starting points; the studio edits them. */
export const DEFAULT_FX: Record<string, number> = {
  USD: 1, GYD: 209, TTD: 6.8, BBD: 2, XCD: 2.7, JMD: 157, SRD: 36, EUR: 0.92, GBP: 0.79, CAD: 1.37,
};
/** Multiply an amount in `from` by this to express it in `to`. 1 when either
 *  currency is unknown to the table — never a silent zero. */
export function fxFactor(from: string | undefined, to: string | undefined, fx?: Record<string, number>): number {
  const a = (from || DEFAULT_RATE_CURRENCY).toUpperCase();
  const b = (to || DEFAULT_RATE_CURRENCY).toUpperCase();
  if (a === b) return 1;
  const table = { ...DEFAULT_FX, ...(fx ?? {}) };
  const fa = table[a], fb = table[b];
  if (!(fa > 0) || !(fb > 0)) return 1;
  return fb / fa;
}
export function fxKnown(ccy: string | undefined, fx?: Record<string, number>): boolean {
  const c = (ccy || DEFAULT_RATE_CURRENCY).toUpperCase();
  return ({ ...DEFAULT_FX, ...(fx ?? {}) })[c] > 0;
}
/** Convert a rate between area units. */
export function rateToUnit(rate: number, from: AreaUnit, to: AreaUnit): number {
  if (from === to) return rate;
  return from === "sqft" ? rate * SQFT_PER_SQM : rate / SQFT_PER_SQM;
}

/** A rate expressed in the unit the caller wants. */
export function rateIn(r: BuildRate, quality: Quality, want: AreaUnit): number {
  const perSqft = r.unit === "sqft" ? r[quality] : r[quality] / SQFT_PER_SQM;
  return want === "sqft" ? perSqft : perSqft * SQFT_PER_SQM;
}

export function findRate(rates: BuildRate[], type?: string): BuildRate {
  return rates.find((r) => r.type === type) ?? rates.find((r) => r.type === "Private Residence") ?? DEFAULT_BUILD_RATES[0];
}

/** The rate for a project. A mixed-use project names several building types;
 *  its rate is the average of theirs, not whichever happened to match a label. */
export function findRateFor(rates: BuildRate[], p: { type?: string; types?: string[] }): BuildRate {
  const direct = rates.find((r) => r.type === p.type);
  if (direct) return direct;
  const parts = (p.types?.length ? p.types : (p.type ?? "").split(/\s*[·/,&+]\s*/)).map((t) => t.trim()).filter(Boolean);
  const hits = parts
    .map((t) => rates.find((r) => r.type === t) ?? rates.find((r) => r.type.toLowerCase().includes(t.toLowerCase()) || t.toLowerCase().includes(r.type.toLowerCase().split(/\s*\/\s*/)[0])))
    .filter((r): r is BuildRate => !!r);
  if (!hits.length) return findRate(rates, p.type);
  if (hits.length === 1) return hits[0];
  const avg = (q: Quality) => Math.round(hits.reduce((s, r) => s + (r.unit === "sqft" ? r[q] : r[q] / SQFT_PER_SQM), 0) / hits.length);
  return { type: hits.map((h) => h.type).join(" + "), unit: "sqft", standard: avg("standard"), premium: avg("premium"), bespoke: avg("bespoke") };
}

export const areaFromBudget = (budget: number, ratePerUnit: number) => (ratePerUnit > 0 ? Math.round(budget / ratePerUnit) : 0);
export const budgetFromArea = (area: number, ratePerUnit: number) => Math.round(area * ratePerUnit);

/* ---- what the studio's own finished work actually cost -------------------- */

export interface LearnedRate { type: string; unit: AreaUnit; perUnit: number; samples: number; from: string[] }

/** Read real cost-per-area out of projects that have both a costed plan and a
 *  known area. Only counts what's actually there — no extrapolation. */
export function learnedRates(projects: Project[]): LearnedRate[] {
  const buckets = new Map<string, { unit: AreaUnit; rates: number[]; from: string[] }>();
  for (const p of projects) {
    const plan = p.costPlan;
    if (!plan?.lines?.length) continue;
    // prefer tendered money over our own rates — it's the truer number
    const total = plan.lines.reduce((s, l) => s + (l.tendered != null ? l.tendered : l.qty * l.rate), 0);
    const area = p.gia ?? 0;
    if (!(total > 0) || !(area > 0)) continue;
    const unit: AreaUnit = (p.giaUnit as AreaUnit) ?? "sqft";
    const key = `${p.type}|${unit}`;
    const b = buckets.get(key) ?? { unit, rates: [] as number[], from: [] as string[] };
    b.rates.push(total / area);
    b.from.push(p.code || p.name);
    buckets.set(key, b);
  }
  const out: LearnedRate[] = [];
  for (const [key, b] of buckets) {
    const sorted = [...b.rates].sort((x, y) => x - y);
    const mid = sorted.length % 2 ? sorted[(sorted.length - 1) / 2] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
    out.push({ type: key.split("|")[0], unit: b.unit, perUnit: Math.round(mid), samples: b.rates.length, from: b.from });
  }
  return out.sort((a, b) => b.samples - a.samples);
}

/* ---- fitting the brief to the money -------------------------------------- */

/* Which spaces can give ground, and which can't. A house without circulation
   or a bathroom isn't a cheaper house, it's an unbuildable one — so those hold
   their size, and the discretionary rooms flex or go. */
const ESSENTIAL = /(circulation|core|stair|lift|corridor|lobby|entry|foyer|bath|wc|washroom|toilet|shower|kitchen|plant|riser|electrical|mechanical|service|store|utility|laundry)/i;
const DISCRETIONARY = /(gym|pool|sauna|spa|cinema|media|theatre|wine|cellar|bar|guest|study|studio|library|games|play|hobby|workshop|terrace|deck|patio|veranda|balcony|garden|courtyard|pergola|gazebo|outdoor)/i;

export type Treatment = "kept" | "reduced" | "dropped";
export interface FittedSpace { space: SpaceReq; treatment: Treatment; area: number; originalArea: number }
export interface FitResult {
  spaces: FittedSpace[];
  totalArea: number;              // the fitted gross
  requestedArea: number;          // what the brief asked for
  shortfall: number;              // requested − fitted (0 when it all fits)
  dropped: number; reduced: number;
  note: string;
}

/** Trim a brief to a target area, honestly and in a defensible order:
 *  discretionary rooms give way first (largest first), then flexible rooms
 *  shrink — but never below `floor` of their briefed size, and never at all for
 *  the spaces a building can't do without. */
export function fitToArea(spaces: SpaceReq[], targetArea: number, floor = 0.7): FitResult {
  const areaOf = (s: SpaceReq) => (s.area || 0) * (s.qty || 1);
  const requested = spaces.reduce((sum, s) => sum + areaOf(s), 0);
  const out: FittedSpace[] = spaces.map((s) => ({ space: s, treatment: "kept" as Treatment, area: areaOf(s), originalArea: areaOf(s) }));

  if (!(targetArea > 0) || requested <= targetArea) {
    return { spaces: out, totalArea: requested, requestedArea: requested, shortfall: 0, dropped: 0, reduced: 0,
      note: requested === 0 ? "No accommodation schedule to size yet." : `The brief fits — ${Math.round(requested).toLocaleString()} against a target of ${Math.round(targetArea).toLocaleString()}.` };
  }

  let over = requested - targetArea;

  // 1) drop discretionary rooms, biggest first — the clearest, most honest cut
  const discretionary = out
    .filter((f) => DISCRETIONARY.test(f.space.name) && !ESSENTIAL.test(f.space.name))
    .sort((a, b) => b.area - a.area);
  for (const f of discretionary) {
    if (over <= 0) break;
    f.treatment = "dropped"; over -= f.area; f.area = 0;
  }

  // 2) shrink what's left that can flex, proportionally, down to the floor
  if (over > 0) {
    const flexible = out.filter((f) => f.treatment === "kept" && !ESSENTIAL.test(f.space.name));
    const flexTotal = flexible.reduce((s, f) => s + f.area, 0);
    const room = flexTotal * (1 - floor);                     // how much they can give in total
    if (flexTotal > 0 && room > 0) {
      const take = Math.min(over, room);
      const factor = (flexTotal - take) / flexTotal;
      for (const f of flexible) { f.area = Math.round(f.area * factor); f.treatment = "reduced"; }
      over -= take;
    }
  }

  const totalArea = out.reduce((s, f) => s + f.area, 0);
  const dropped = out.filter((f) => f.treatment === "dropped").length;
  const reduced = out.filter((f) => f.treatment === "reduced").length;
  const shortfall = Math.max(0, Math.round(requested - totalArea - (requested - targetArea)));

  const note = over > 0.5
    ? `This budget can't carry the brief. Even after removing ${dropped} discretionary space${dropped === 1 ? "" : "s"} and reducing ${reduced}, it's about ${Math.round(over).toLocaleString()} short — the brief needs cutting, or the budget raising.`
    : dropped || reduced
      ? `Fits by removing ${dropped} discretionary space${dropped === 1 ? "" : "s"}${reduced ? ` and reducing ${reduced} other${reduced === 1 ? "" : "s"}` : ""}. Circulation, bathrooms, kitchens and plant hold their size — a building still has to work.`
      : "The brief fits this budget as briefed.";

  return { spaces: out, totalArea, requestedArea: requested, shortfall, dropped, reduced, note };
}
